import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Leaf, Globe2 } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const PILLARS = [
  { icon: Leaf, title: "Authentic Sourcing", body: "Direct partnerships with cooperatives across Kerala, Rajasthan, Gujarat, Karnataka and Tamil Nadu — each region chosen for the spice it grows best." },
  { icon: Sparkles, title: "Modern Processing", body: "Clean, mechanised facilities with sortex sorting, controlled grinding and packaging built for both export and retail formats." },
  { icon: Globe2, title: "Global Discipline", body: "Every shipment carries the documentation, quality reports and consistency international buyers expect from a premium Indian partner." },
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About DeepRoots Spice"
        title="A modern Indian export house."
        italicWord="Indian"
        subtitle="We are a manufacturer, supplier and exporter of premium Indian spices — built on authentic sourcing, modern processing discipline, and a commitment to long-term buyer relationships across the world."
      />

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
            <span className="overline">Our Philosophy</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05] text-brand-charcoal">Spice is heritage. Trade is trust.</h2>
            <span className="divider-gold" />
          </Reveal>
          <Reveal delay={150} className="lg:col-span-6 lg:col-start-7 space-y-6 text-brand-charcoal/75 text-[17px] leading-[1.8]">
            <p>India's relationship with spices is older than the recorded history of most nations. Turmeric from Erode. Pepper from the Malabar coast. Cardamom from the hills of Idukki. Every region has spent centuries perfecting what it grows — and modern export work is about honouring that expertise while meeting the exacting standards of international commerce.</p>
            <p>DeepRoots Spice exists at that intersection. We work directly with cultivators and cooperatives in the country's most respected spice belts, process at facilities aligned with modern food-safety frameworks, and package for the realities of ocean freight and retail shelves alike.</p>
            <p>What our buyers repeatedly tell us is that the spice matters — but the reliability behind it matters more. Consistent moisture percentages. Colour matched to specification. Clean export documentation. Containers that move on time. That is the discipline we build our brand around.</p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32 bg-brand-ivory-warm">
        <div className="container-luxe">
          <Reveal className="max-w-xl mb-16">
            <span className="overline">Three Pillars</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">The way we work.</h2>
            <span className="divider-gold" />
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 100} className="p-8 bg-brand-ivory border border-brand-charcoal/8 hover:border-brand-gold/60 transition-colors">
                <div className="w-12 h-12 border border-brand-green/40 flex items-center justify-center mb-6 text-brand-green">
                  <p.icon className="w-5 h-5" />
                </div>
                <h4 className="font-serif text-2xl mb-3 text-brand-charcoal">{p.title}</h4>
                <p className="text-brand-charcoal/60 text-[15px] leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="py-24 md:py-32 bg-brand-green text-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="/images/hero/hero-quality.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxe relative grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="overline text-brand-gold-soft">Our Commitment</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Long-term relationships, container after container.</h2>
            <span className="block w-16 h-px bg-brand-gold my-6" />
            <p className="text-brand-ivory/75 leading-relaxed max-w-lg">We measure our success by repeat orders — not first shipments. Our export coordinators build ongoing relationships with each buyer, understanding your market, packaging, seasonality and margins as well as our own.</p>
          </Reveal>
          <Reveal delay={150} className="grid grid-cols-2 gap-6">
            {[
              { k: "Direct", v: "Farm-to-container sourcing" },
              { k: "Verified", v: "Lab-tested every lot" },
              { k: "Flexible", v: "Bulk to retail packaging" },
              { k: "Global", v: "Multi-continent shipping" },
            ].map((s) => (
              <div key={s.k} className="border-l border-brand-gold/60 pl-5">
                <div className="font-serif text-3xl text-brand-gold">{s.k}</div>
                <div className="text-brand-ivory/70 text-[13px] tracking-wide mt-2">{s.v}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-ivory">
        <div className="container-luxe text-center max-w-2xl mx-auto">
          <h3 className="font-serif text-4xl text-brand-charcoal">Begin a conversation.</h3>
          <p className="mt-6 text-brand-charcoal/60 leading-relaxed">Share your requirements and we will introduce ourselves properly — with samples, specifications and pricing tailored to your market.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/request-quote" className="luxe-btn-primary" data-testid="about-cta-quote">Request Quote <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" className="luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory" data-testid="about-cta-contact">Contact us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
